/**
 * Line of sight.
 *
 * One rule for "can A see B": the segment between their eyes, LOS_EYE_HEIGHT
 * above the exact terrain, must not pass through any planet's terrain. Planet
 * bodies, craters and ridges all block the same way — there is no separate
 * hemisphere rule, blocking-sphere approximation or per-caller eye offset.
 * Used by fog (spotting, exposure), bots (cover scoring) and projectile
 * visibility. Mirrored in wasm-physics/src/lib.rs; tests/wasmParity.test.ts
 * proves the two agree.
 *
 * Revealing a whole planet's terrain (thousands of vertices) cannot afford a
 * raycast per vertex, so it uses the cheaper analytic pieces defined here:
 *   - the own-planet reveal angle (survey horizon, never narrower than the
 *     arc over which another player could be spotted),
 *   - the own-planet skyline (which remote directions the ground around the
 *     viewer hides — replaces treating the viewer's planet as a sphere, which
 *     blinded anyone standing in a crater),
 *   - the remote planet's terrain self-shadow mask.
 */

import type { Vec3, PlanetState, PlayerState } from '../types';
import { LOS_EYE_HEIGHT, LOS_SKYLINE_BINS } from '../constants';
import { getSurfaceRadius, raycastTerrain } from './surface';
import { dacos, dasin, datan2 } from './dmath';
import { simWork } from './workCounters';

/** Stop the LOS ray this short of the target so the target's own ground is never a hit. */
export const LOS_END_EPSILON = 0.01;

// =============================================================================
// Eye points
// =============================================================================

/** World point LOS_EYE_HEIGHT above the terrain in `surfaceDir` on `planet`. */
export function eyePointAt(planet: PlanetState, surfaceDir: Vec3, eyeHeight: number = LOS_EYE_HEIGHT): Vec3 {
  const dl = Math.sqrt(surfaceDir.x * surfaceDir.x + surfaceDir.y * surfaceDir.y + surfaceDir.z * surfaceDir.z);
  if (dl < 1e-12) return { ...planet.position };
  const r = getSurfaceRadius(planet, surfaceDir) + eyeHeight;
  return {
    x: planet.position.x + surfaceDir.x / dl * r,
    y: planet.position.y + surfaceDir.y / dl * r,
    z: planet.position.z + surfaceDir.z / dl * r,
  };
}

/** 63.1d step 2: a point `height` above a seated unit's stand in its hatch (`seatPos`), up its planet's radial. */
export function abovePoint(at: Vec3, planet: PlanetState, height: number): Vec3 {
  const ux = at.x - planet.position.x, uy = at.y - planet.position.y, uz = at.z - planet.position.z;
  const l = Math.sqrt(ux * ux + uy * uy + uz * uz) || 1;
  return { x: at.x + (ux / l) * height, y: at.y + (uy / l) * height, z: at.z + (uz / l) * height };
}

/** A player's eye: where they look from and the point others must see. */
export function losEyePoint(player: PlayerState, planets: PlanetState[], eyeHeight: number = LOS_EYE_HEIGHT): Vec3 {
  if (player.airborne && player.airbornePos) return player.airbornePos;
  if (player.seatPos) return abovePoint(player.seatPos, planets[player.planetIndex], eyeHeight);   // 63.1d step 2: in its hatch
  return eyePointAt(planets[player.planetIndex], player.surfaceDir, eyeHeight);
}

/** A player's feet: exact terrain point (markers, distance checks). */
export function playerSurfacePoint(player: PlayerState, planets: PlanetState[]): Vec3 {
  if (player.airborne && player.airbornePos) return player.airbornePos;
  const planet = planets[player.planetIndex];
  const d = player.surfaceDir;
  const dl = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
  if (dl < 1e-12) return { ...planet.position };
  const r = getSurfaceRadius(planet, d);
  return { x: planet.position.x + d.x / dl * r, y: planet.position.y + d.y / dl * r, z: planet.position.z + d.z / dl * r };
}

/** Planet radius including its tallest terrain: a sphere nothing of it pokes out of. */
export function getEffectiveBlockingRadius(planet: PlanetState): number {
  let maxH = 0;
  const hm = planet.heightmap;
  for (let i = 0; i < hm.length; i++) {
    if (hm[i] > maxH) maxH = hm[i];
  }
  return planet.radius + maxH;
}

// =============================================================================
// Point-to-point line of sight
// =============================================================================

function lexLess(a: Vec3, b: Vec3): boolean {
  if (a.x !== b.x) return a.x < b.x;
  if (a.y !== b.y) return a.y < b.y;
  return a.z < b.z;
}

/**
 * True when nothing solid lies between `a` and `b`. The ray is always cast
 * from the lexicographically smaller point so the answer is symmetric.
 */
export function hasLineOfSight(a: Vec3, b: Vec3, planets: PlanetState[]): boolean {
  simWork.sightLines++;   // 62.53: counted for the frame budget
  const from = lexLess(a, b) ? a : b;
  const to = from === a ? b : a;
  const dx = to.x - from.x, dy = to.y - from.y, dz = to.z - from.z;
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (dist < 1e-6) return true;
  const maxDist = dist - LOS_END_EPSILON;
  if (maxDist <= 0) return true;
  return raycastTerrain(from, { x: dx / dist, y: dy / dist, z: dz / dist }, maxDist, planets) === null;
}

// =============================================================================
// Own-planet reveal
// =============================================================================

/**
 * Angular radius (from the planet centre) of the terrain a viewer standing on
 * their own planet knows. The survey horizon treats the eye as at least one
 * planet radius up, so every planet size shows at least a 60° cap; it is
 * never narrower than the arc over which another player could be spotted, so
 * nobody is ever seen standing on terrain that is still dark.
 */
export function ownPlanetRevealAngle(planet: PlanetState, eyeAboveSphere: number): number {
  const R = planet.radius;
  const survey = dacos(R / (R + Math.max(eyeAboveSphere, R)));
  const mutual = 2 * dacos(R / (R + LOS_EYE_HEIGHT));
  return Math.max(survey, mutual);
}

/** Extra angle a vertex at terrain height `h` peeks over the horizon. */
export function terrainPeekAngle(planet: PlanetState, h: number): number {
  const R = planet.radius;
  return dacos(R / (R + Math.max(0, h)));
}

// =============================================================================
// Own-planet skyline
// =============================================================================

export interface Skyline {
  eye: Vec3;
  up: Vec3;
  t1: Vec3;
  t2: Vec3;
  /** Highest terrain elevation (radians above horizontal) per azimuth bin. */
  bins: Float64Array;
}

function skylineFrame(eye: Vec3, center: Vec3): { up: Vec3; t1: Vec3; t2: Vec3 } {
  const ux0 = eye.x - center.x, uy0 = eye.y - center.y, uz0 = eye.z - center.z;
  const ul = Math.sqrt(ux0 * ux0 + uy0 * uy0 + uz0 * uz0) || 1;
  const up = { x: ux0 / ul, y: uy0 / ul, z: uz0 / ul };
  const ref = Math.abs(up.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  // t1 = normalize(up × ref), t2 = up × t1
  const cx = up.y * ref.z - up.z * ref.y, cy = up.z * ref.x - up.x * ref.z, cz = up.x * ref.y - up.y * ref.x;
  const cl = Math.sqrt(cx * cx + cy * cy + cz * cz) || 1;
  const t1 = { x: cx / cl, y: cy / cl, z: cz / cl };
  const t2 = { x: up.y * t1.z - up.z * t1.y, y: up.z * t1.x - up.x * t1.z, z: up.x * t1.y - up.y * t1.x };
  return { up, t1, t2 };
}

function skylineBin(sky: Skyline, v: Vec3): { bin: number; el: number } {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  const s = Math.max(-1, Math.min(1, (v.x * sky.up.x + v.y * sky.up.y + v.z * sky.up.z) / len));
  const el = dasin(s);
  const az = datan2(
    v.x * sky.t2.x + v.y * sky.t2.y + v.z * sky.t2.z,
    v.x * sky.t1.x + v.y * sky.t1.y + v.z * sky.t1.z,
  );
  let bin = Math.floor((az + Math.PI) / (2 * Math.PI) * LOS_SKYLINE_BINS);
  if (bin < 0) bin = 0; else if (bin >= LOS_SKYLINE_BINS) bin = LOS_SKYLINE_BINS - 1;
  return { bin, el };
}

/** Most samples along one triangle edge; nearby rim edges span many bins. */
const SKYLINE_EDGE_SAMPLES_MAX = 64;

/**
 * The silhouette of the viewer's own planet as seen from `eye`: for each
 * azimuth, the highest elevation any of its terrain reaches. The highest point
 * of a flat triangle in any azimuth lies on its edges, so edges are sampled
 * densely enough to touch every bin they cross (vertices alone leave gaps in
 * the nearby crater rim). Bins start at the bare-sphere limb so sparse meshes
 * still behave like a sphere.
 */
export function buildSkyline(eye: Vec3, planet: PlanetState): Skyline {
  const { up, t1, t2 } = skylineFrame(eye, planet.position);
  const sky: Skyline = { eye, up, t1, t2, bins: new Float64Array(LOS_SKYLINE_BINS) };
  const dx = eye.x - planet.position.x, dy = eye.y - planet.position.y, dz = eye.z - planet.position.z;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const limb = d > planet.radius ? -dacos(planet.radius / d) : -Math.PI / 2;
  sky.bins.fill(limb);

  const bp = planet.basePositions;
  const hm = planet.heightmap;
  const n = hm.length;
  // Displaced vertex positions relative to the eye
  const vx = new Float64Array(n), vy = new Float64Array(n), vz = new Float64Array(n);
  const az = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bl = Math.sqrt(bx * bx + by * by + bz * bz);
    const s = bl < 1e-9 ? 1 : 1 + hm[i] / bl;
    vx[i] = planet.position.x + bx * s - eye.x;
    vy[i] = planet.position.y + by * s - eye.y;
    vz[i] = planet.position.z + bz * s - eye.z;
    const v = { x: vx[i], y: vy[i], z: vz[i] };
    if (v.x * v.x + v.y * v.y + v.z * v.z < 1e-12) { az[i] = 0; continue; }
    const r = skylineBin(sky, v);
    az[i] = datan2(v.x * t2.x + v.y * t2.y + v.z * t2.z, v.x * t1.x + v.y * t1.y + v.z * t1.z);
    if (r.el > sky.bins[r.bin]) sky.bins[r.bin] = r.el;
  }

  if (n % 3 !== 0) return sky; // not a triangle mesh: vertices only
  const binWidth = (2 * Math.PI) / LOS_SKYLINE_BINS;
  const sample = { x: 0, y: 0, z: 0 };
  for (let t = 0; t < n; t += 3) {
    for (let e = 0; e < 3; e++) {
      const i0 = t + e, i1 = t + ((e + 1) % 3);
      let span = Math.abs(az[i0] - az[i1]);
      if (span > Math.PI) span = 2 * Math.PI - span;
      let k = Math.ceil(span / binWidth);
      if (k < 1) k = 1; else if (k > SKYLINE_EDGE_SAMPLES_MAX) k = SKYLINE_EDGE_SAMPLES_MAX;
      if (k === 1) continue; // endpoints already binned
      for (let j = 1; j < k; j++) {
        const f = j / k;
        sample.x = vx[i0] + (vx[i1] - vx[i0]) * f;
        sample.y = vy[i0] + (vy[i1] - vy[i0]) * f;
        sample.z = vz[i0] + (vz[i1] - vz[i0]) * f;
        if (sample.x * sample.x + sample.y * sample.y + sample.z * sample.z < 1e-12) continue;
        const r = skylineBin(sky, sample);
        if (r.el > sky.bins[r.bin]) sky.bins[r.bin] = r.el;
      }
    }
  }
  return sky;
}

/** True when the own-planet terrain hides a distant `target` from the skyline's eye. */
export function skylineBlocks(sky: Skyline, target: Vec3): boolean {
  const v = { x: target.x - sky.eye.x, y: target.y - sky.eye.y, z: target.z - sky.eye.z };
  if (v.x * v.x + v.y * v.y + v.z * v.z < 1e-12) return false;
  const { bin, el } = skylineBin(sky, v);
  return el <= sky.bins[bin];
}

// =============================================================================
// Remote-planet terrain self-shadow
// =============================================================================

/**
 * Which vertices of a remote planet hide behind that planet's own ridges from
 * `eye`: an orthographic depth buffer of gridSize² cells perpendicular to the
 * view direction. Port of compute_terrain_shadow_mask (lib.rs) — keep the
 * 1.5% epsilon, larger values cause artifacts.
 */
export function terrainShadowMask(eye: Vec3, planet: PlanetState, gridSize = 128): Uint8Array {
  const n = planet.heightmap.length;
  const bp = planet.basePositions;
  const hm = planet.heightmap;

  let vx = planet.position.x - eye.x, vy = planet.position.y - eye.y, vz = planet.position.z - eye.z;
  const vl = Math.sqrt(vx * vx + vy * vy + vz * vz) || 1;
  vx /= vl; vy /= vl; vz /= vl;

  let rx = 1, ry = 0, rz = 0;
  if (Math.abs(vx * rx + vy * ry + vz * rz) > 0.9) { rx = 0; ry = 1; rz = 0; }
  // proj_right = view × ref, proj_up = view × proj_right (both normalized)
  let prx = vy * rz - vz * ry, pry = vz * rx - vx * rz, prz = vx * ry - vy * rx;
  const prl = Math.sqrt(prx * prx + pry * pry + prz * prz) || 1;
  prx /= prl; pry /= prl; prz /= prl;
  let pux = vy * prz - vz * pry, puy = vz * prx - vx * prz, puz = vx * pry - vy * prx;
  const pul = Math.sqrt(pux * pux + puy * puy + puz * puz) || 1;
  pux /= pul; puy /= pul; puz /= pul;

  const effRadius = getEffectiveBlockingRadius(planet);
  const invScale = gridSize / (2 * effRadius);
  const halfGrid = gridSize * 0.5;
  const epsilon = effRadius * 0.015;

  const minZ = new Float64Array(gridSize * gridSize).fill(Infinity);
  const cell = new Int32Array(n);
  const depth = new Float64Array(n);

  for (let i = 0; i < n; i++) {
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bl = Math.sqrt(bx * bx + by * by + bz * bz);
    const h = hm[i];
    const px0 = bx + bx / bl * h, py0 = by + by / bl * h, pz0 = bz + bz / bl * h;
    const px = (px0 * prx + py0 * pry + pz0 * prz) * invScale + halfGrid;
    const py = (px0 * pux + py0 * puy + pz0 * puz) * invScale + halfGrid;
    const z = px0 * vx + py0 * vy + pz0 * vz;
    const gx = Math.trunc(px), gy = Math.trunc(py);
    depth[i] = z;
    if (gx >= 0 && gx < gridSize && gy >= 0 && gy < gridSize) {
      const c = gy * gridSize + gx;
      cell[i] = c;
      if (z < minZ[c]) minZ[c] = z;
    } else {
      cell[i] = -1;
    }
  }

  const shadow = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const c = cell[i];
    if (c >= 0 && depth[i] > minZ[c] + epsilon) shadow[i] = 1;
  }
  return shadow;
}
